import React, { useMemo, useEffect } from "react";
import useStore from "@/zustand/store";
import JammeCoinIcon from "@/assets/JammeCoinLogo.svg"
import { ArrowRight, BatteryFull, Bookmark, ChevronRight, Ellipsis, Forward, Heart, Languages, Signal, Wifi } from "lucide-react";
import { getUserInfoFromLocalStorage } from "@/utils/localStorage";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress"

const Preview: React.FC = () => {
  const { adText, adImage, backgroundMedia } = useStore();
  const { companyName, logo } = getUserInfoFromLocalStorage();  // Access the auth details

  // Memoize the object URL creation to avoid unnecessary re-renders
  const adImageURL = useMemo(() => (adImage ? URL.createObjectURL(adImage) : ""), [adImage]);
  const backgroundMediaURL = useMemo(() => (backgroundMedia ? URL.createObjectURL(backgroundMedia) : ""), [backgroundMedia]);
  return (
    <div className="relative flex items-center justify-center dark:border border-gray-300 rounded-3xl">
      {/* Video Background */}
      {backgroundMedia && backgroundMedia.type.startsWith("video") && (
        <video
          src={backgroundMediaURL}
          autoPlay
          muted
          loop
          className="absolute top-0 left-0 w-full h-full object-cover rounded-3xl"
        />
      )}

      {/* Image Background */}
      {backgroundMedia && backgroundMedia.type.startsWith("image") && (
        <div
          className="absolute top-0 left-0 w-full h-full bg-cover bg-center rounded-3xl"
          style={{ backgroundImage: `url(${backgroundMediaURL})` }}
        />
      )}
      {/* Main Content */}
      <div className="relative w-[350px] lg:w-[360px] overflow-hidden font-Chillax">
        <div
          className="text-white flex flex-col items-center p-4 rounded-3xl bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `
              linear-gradient(179.98deg, rgba(13, 6, 0, 0.09) 0.01%, rgba(20, 10, 0, 0.69) 19.42%, rgba(18, 9, 0, 0.84) 56.86%, #100800 99.99%),
  linear-gradient(to bottom, rgba(34, 34, 34, 0.5), rgba(240, 185, 11, 0.5))
            `,
          }}
        >
          <div className="flex items-center justify-between gap-[160px] lg:gap-[200px] pb-5 overflow-hidden">
            <p className="font-medium text-[15px] ml-2">9:41</p>
            <div className="flex gap-2">
              <Signal size={20} />
              <Wifi size={20} />
              <BatteryFull size={20} />
            </div>
          </div>
          <div className="flex items-center w-[300px] lg:w-[360px] mb-2 px-5">
            <button className="text-white text-xl mr-auto">&larr;</button>
            <h1 className="text-sm mx-auto">Ads</h1>
            <button className="text-white text-xl ml-auto">&bull;&bull;&bull;</button>
          </div>
          <div className="bg-[#FFF7EF] rounded-xl w-64 lg:w-80 h-80 mt-8">
            {adImage && adImage.type.startsWith("image") && (
              <img
                src={adImageURL}
                alt="Ad"
                className="object-cover h-full w-full rounded-xl"
              />
            )}
            {adImage && adImage.type.startsWith("video") && (
              <video autoPlay muted loop className="object-cover h-full w-full rounded-xl">
                <source src={adImageURL} type={adImage.type} />
                Your browser does not support the video element.
              </video>
            )}
          </div>
          <p className="mt-6 text-sm text-center tracking-tight">
            Listen without skipping for <br />
            <span className="inline-flex items-center bg-[#FAF2D3] rounded-full px-2 py-0.5 text-black mt-2">
              <img src={JammeCoinIcon} alt="JammeCoinIcon" className="w-4 h-4 mr-1" />
              <p className="text-xs font-medium">10 coins</p>
            </span>
          </p>
          <div className="absolute right-4 top-[68%] -translate-y-[10%] flex flex-col gap-4 items-center">
            <div className="flex flex-col items-center justify-center">
              <Heart />
              <p className="text-xs font-normal">12.3k</p>
            </div>
            <Bookmark />
            <Languages />
            <Forward />
            <Ellipsis />
          </div>
          <div className="flex flex-col items-start gap-2 -ml-12 mt-[100px] mb-[30px]">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6 border cursor-pointer">
                <AvatarImage
                  src={logo || "https://png.pngtree.com/png-clipart/20230927/original/pngtree-man-avatar-image-for-profile-png-image_13001882.png"}
                  alt="User Logo"
                  className="object-cover rounded-full w-6 h-6"
                />
                <AvatarFallback>J</AvatarFallback>
              </Avatar>
              <p className="text-sm font-medium leading-none">
                {companyName || "Company Name"}
              </p>
            </div>
            <Button variant="primary" className="bg-Primary  w-52 lg:w-64 flex justify-between items-center px-4 text-black rounded-full">
              <span>{adText || "----"}</span>
              <ChevronRight />
            </Button>
            <Progress value={20} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preview;
